import React, { useEffect, useRef, useState } from 'react'
import { Button, Tooltip, Upload } from 'antd'
import { ClearOutlined, DeleteOutlined, DownloadOutlined, UndoOutlined, UploadOutlined } from '@ant-design/icons'
import { FaFingerprint } from 'react-icons/fa'
import { toast } from 'react-toastify'
import PadFirma from './PadFirma'
import {
  LADO_DESCARGA,
  cargarFuentesHuella,
  dibujarSelloHuella,
} from './huellaUtils'
import logoAuteco from '../../assets/images/logoaut.png'
import './HuellaContainer.scss'

const LADO_PREVIA = 900
// La plantilla de diseño se recuerda en este navegador (si cabe en el storage).
const CLAVE_PLANTILLA = 'huella.plantilla'

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })

/**
 * Módulo Huella: la persona firma y se genera el sello "Esta moto tiene mi
 * huella" con su firma formando la huella. Se descarga en PNG.
 */
const HuellaContainer = () => {
  const previaRef = useRef(null)
  const [trazos, setTrazos] = useState([])
  const [plantilla, setPlantilla] = useState(null)
  const [logo, setLogo] = useState(null)
  const [fuentesListas, setFuentesListas] = useState(false)
  const [descargando, setDescargando] = useState(false)

  useEffect(() => {
    cargarFuentesHuella().then(() => setFuentesListas(true))
    cargarImagen(logoAuteco).then(setLogo).catch(() => {})
    try {
      const guardada = window.localStorage.getItem(CLAVE_PLANTILLA)
      if (guardada) cargarImagen(guardada).then(setPlantilla).catch(() => {})
    } catch {
      // Sin storage no se recuerda la plantilla.
    }
  }, [])

  // Vista previa: se redibuja con cada trazo o ajuste (con un pequeño respiro
  // para no recalcular en cada movimiento del slider).
  useEffect(() => {
    if (!fuentesListas || !previaRef.current) return undefined
    const id = setTimeout(() => {
      dibujarSelloHuella(previaRef.current, { trazos, lado: LADO_PREVIA, plantilla, logo })
    }, 60)
    return () => clearTimeout(id)
  }, [trazos, plantilla, logo, fuentesListas])

  const descargar = async () => {
    setDescargando(true)
    try {
      const canvas = document.createElement('canvas')
      dibujarSelloHuella(canvas, { trazos, lado: LADO_DESCARGA, plantilla, logo })
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (!blob) throw new Error('No se pudo generar la imagen')
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'huella-moto-perfecta.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error generando la huella:', error)
      toast.error('No se pudo generar la imagen')
    } finally {
      setDescargando(false)
    }
  }

  const subirPlantilla = (file) => {
    const lector = new FileReader()
    lector.onload = async () => {
      try {
        const img = await cargarImagen(lector.result)
        setPlantilla(img)
        try {
          window.localStorage.setItem(CLAVE_PLANTILLA, lector.result)
        } catch {
          toast.info('La plantilla es muy pesada para recordarla; se usará solo en esta sesión')
        }
      } catch {
        toast.error('No se pudo leer la imagen de la plantilla')
      }
    }
    lector.readAsDataURL(file)
    return false
  }

  const quitarPlantilla = () => {
    setPlantilla(null)
    try {
      window.localStorage.removeItem(CLAVE_PLANTILLA)
    } catch {
      // Nada que limpiar.
    }
  }

  return (
    <div className="huella">
      <h1 className="huella__titulo">
        <FaFingerprint /> Huella
      </h1>

      <div className="huella__cuerpo">
        <section className="huella__panel">
          <header className="huella__seccion">
            <span>1. Firma</span>
            <span className="huella__acciones">
              <Tooltip title="Deshacer el último trazo">
                <Button size="small" icon={<UndoOutlined />} disabled={!trazos.length} onClick={() => setTrazos((t) => t.slice(0, -1))} />
              </Tooltip>
              <Button size="small" icon={<ClearOutlined />} disabled={!trazos.length} onClick={() => setTrazos([])}>
                Limpiar
              </Button>
            </span>
          </header>
          <PadFirma trazos={trazos} onCambio={setTrazos} />
          <p className="huella__nota">Firma con el mouse, el dedo o un lápiz. La huella se arma sola con tu firma.</p>

          <header className="huella__seccion">
            <span>2. Plantilla de diseño (opcional)</span>
          </header>
          <p className="huella__nota">
            Para que quede idéntica al diseño oficial, carga el PNG cuadrado del sello con la zona de la huella
            transparente. Reemplaza el aro, los textos, la cinta y el logo dibujados.
          </p>
          <div className="huella__acciones">
            <Upload accept="image/png" showUploadList={false} beforeUpload={subirPlantilla}>
              <Button size="small" icon={<UploadOutlined />}>
                {plantilla ? 'Cambiar plantilla' : 'Cargar plantilla'}
              </Button>
            </Upload>
            {plantilla && (
              <Button size="small" danger icon={<DeleteOutlined />} onClick={quitarPlantilla}>
                Quitar
              </Button>
            )}
          </div>
        </section>

        <section className="huella__previa">
          <canvas ref={previaRef} width={LADO_PREVIA} height={LADO_PREVIA} />
          <Button
            type="primary"
            size="large"
            icon={<DownloadOutlined />}
            onClick={descargar}
            loading={descargando}
            disabled={!trazos.length || !fuentesListas}
          >
            Descargar PNG ({LADO_DESCARGA}×{LADO_DESCARGA})
          </Button>
        </section>
      </div>
    </div>
  )
}

export default HuellaContainer
